import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { ReviewPage } from '@shared/types';

interface Props {
  pages: ReviewPage[];
  active: number;
  onChange: (index: number) => void;
  approvedPages?: Set<number>;
  onToggleApproved?: (index: number) => void;
}

function shortLabel(p: ReviewPage) {
  if (p.kind === 'removed') return `~ ${p.title}`;
  return p.title;
}

function ApprovalCircle({
  approved,
  onToggle,
}: {
  approved: boolean;
  onToggle: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={approved ? 'Mark as not reviewed' : 'Mark as reviewed'}
      className={[
        'w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors',
        approved
          ? 'bg-green-500 border-green-500'
          : 'bg-transparent border-gray-400 hover:border-gray-600',
      ].join(' ')}
    >
      {approved && (
        <svg
          viewBox="0 0 12 12"
          className="w-2.5 h-2.5 text-white"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="2,6 5,9 10,3" />
        </svg>
      )}
    </button>
  );
}

export function PageTabs({ pages, active, onChange, approvedPages, onToggleApproved }: Props) {
  if (pages.length === 0) return null;
  const showApproval = pages.length > 1 && onToggleApproved != null;

  const sorted = pages
    .map((p, i) => ({ page: p, originalIndex: i }))
    .sort((a, b) => {
      const aApproved = approvedPages?.has(a.originalIndex) ?? false;
      const bApproved = approvedPages?.has(b.originalIndex) ?? false;
      if (aApproved === bApproved) return a.originalIndex - b.originalIndex;
      return aApproved ? 1 : -1;
    });

  return (
    <Tabs
      value={String(active)}
      onValueChange={(v) => onChange(Number(v))}
      className="w-full min-w-0"
    >
      <div className="overflow-x-auto">
        <TabsList className="justify-start flex w-max">
          {sorted.map(({ page: p, originalIndex: i }) => (
            <TabsTrigger key={i} value={String(i)} className="whitespace-nowrap flex items-center gap-1.5">
              {showApproval && (
                <ApprovalCircle
                  approved={approvedPages?.has(i) ?? false}
                  onToggle={(e) => {
                    e.stopPropagation();
                    onToggleApproved(i);
                  }}
                />
              )}
              {shortLabel(p)}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>
    </Tabs>
  );
}
